import { css, html, nothing, type PropertyValues } from 'lit';
import { query, state } from 'lit/decorators.js';

import { renderUniLit } from '../../../core/utils/uni-component/uni-component.js';
import { DataViewUIBase } from '../../../core/view/data-view-base.js';
import type {
  ChartAggregation,
  ChartHeight,
  ChartSort,
  ChartType,
} from '../define.js';
import type { ChartViewUILogic } from './chart-view-ui-logic.js';

type ChartJs = typeof import('chart.js/auto').default;
type ChartInstance = InstanceType<ChartJs>;

const PALETTE = [
  '#aa8adc',
  '#4fb3a9',
  '#f2a65a',
  '#6c9ee8',
  '#e57fa3',
  '#8bc34a',
  '#f2d45c',
  '#9aa5b1',
];

const HEIGHTS: Record<ChartHeight, number> = { S: 200, M: 300, L: 420 };

const TYPE_LABELS: [ChartType, string][] = [
  ['bar', 'Balken'],
  ['horizontal-bar', 'Balken (liegend)'],
  ['line', 'Linie'],
  ['pie', 'Kuchen'],
  ['doughnut', 'Ring'],
];

const SORT_LABELS: [ChartSort, string][] = [
  ['natural', 'Reihenfolge der Optionen'],
  ['desc', 'Wert absteigend'],
  ['asc', 'Wert aufsteigend'],
];

const HEIGHT_LABELS: [ChartHeight, string][] = [
  ['S', 'Klein'],
  ['M', 'Mittel'],
  ['L', 'Groß'],
];

const styles = css`
  affine-data-view-chart {
    display: block;
    box-sizing: border-box;
    width: 100%;
    padding: 8px 0;
  }
  .dv-chart-toggle {
    margin-bottom: 8px;
    padding: 4px 10px;
    font-size: 12px;
    color: var(--affine-text-secondary-color);
    background: transparent;
    border: 1px solid var(--affine-border-color);
    border-radius: 6px;
    cursor: pointer;
  }
  .dv-chart-controls[hidden] {
    display: none;
  }
  .dv-chart-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
  }
  .dv-chart-controls label {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 11px;
    color: var(--affine-text-secondary-color);
  }
  .dv-chart-controls select {
    max-width: 180px;
    padding: 4px 6px;
    font-size: 13px;
    color: var(--affine-text-primary-color);
    background: var(--affine-background-primary-color);
    border: 1px solid var(--affine-border-color);
    border-radius: 6px;
  }
  .dv-chart-canvas {
    position: relative;
    width: 100%;
  }
  .dv-chart-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 14px;
    color: var(--affine-text-secondary-color);
  }
  @media (max-width: 600px) {
    .dv-chart-controls select {
      max-width: 44vw;
    }
  }
`;

const numberFormat = new Intl.NumberFormat('de-CH', { maximumFractionDigits: 1 });

const NUMERIC_TYPES = new Set(['number', 'progress', 'rating']);

export class ChartViewUI extends DataViewUIBase<ChartViewUILogic> {
  static override styles = styles;

  private chart?: ChartInstance;
  private chartJs?: ChartJs;
  private loading?: Promise<void>;

  @query('canvas')
  accessor canvas: HTMLCanvasElement | null = null;

  /** Controls start collapsed on narrow screens so the chart is visible at once. */
  @state()
  accessor controlsOpen = !window.matchMedia('(max-width: 600px)').matches;

  private get view() {
    return this.logic.view;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.chart?.destroy();
    this.chart = undefined;
  }

  override updated(_changed: PropertyValues) {
    void this.drawChart();
  }

  private async drawChart() {
    const canvas = this.canvas;
    if (!canvas) {
      this.chart?.destroy();
      this.chart = undefined;
      return;
    }
    if (!this.chartJs) {
      this.loading ??= import('chart.js/auto').then(module => {
        this.chartJs = module.default;
      });
      await this.loading;
      if (!this.isConnected) return;
    }
    const Chart = this.chartJs;
    if (!Chart || this.canvas !== canvas) return;

    const data = this.view.data$.value;
    const type = data?.chartType ?? 'bar';
    const points = this.view.chartData$.value;
    const labels = points.map(p => p.label);
    const values = points.map(p => p.value);
    const isRound = type === 'pie' || type === 'doughnut';
    const style = getComputedStyle(this);
    const textColor =
      style.getPropertyValue('--affine-text-primary-color').trim() || '#444';
    const gridColor =
      style.getPropertyValue('--affine-border-color').trim() || '#ddd';
    const colors = labels.map((_, i) => PALETTE[i % PALETTE.length]);

    // Chart.js cannot change the chart type in place; rebuild if it differs.
    const kind = isRound ? type : type === 'line' ? 'line' : 'bar';
    if (this.chart && (this.chart.config as { type?: string }).type !== kind) {
      this.chart.destroy();
      this.chart = undefined;
    }

    const dataset = {
      data: values,
      backgroundColor: isRound
        ? colors
        : type === 'line'
          ? 'rgba(170,138,220,0.25)'
          : PALETTE[0],
      borderColor: isRound ? 'transparent' : PALETTE[0],
      borderWidth: type === 'line' ? 3 : 0,
      borderRadius: type === 'line' || isRound ? 0 : 6,
      fill: type === 'line',
      tension: 0.3,
      pointRadius: 4,
      pointBackgroundColor: PALETTE[0],
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 24, right: type === 'horizontal-bar' ? 32 : 8 } },
      animation: { duration: 250 },
      indexAxis: type === 'horizontal-bar' ? ('y' as const) : ('x' as const),
      plugins: {
        legend: {
          display: isRound,
          position: (canvas.clientWidth < 520 ? 'bottom' : 'right') as 'bottom' | 'right',
          labels: { color: textColor },
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx: { parsed: number | { x: number; y: number } }) => {
              const v =
                typeof ctx.parsed === 'number'
                  ? ctx.parsed
                  : type === 'horizontal-bar'
                    ? ctx.parsed.x
                    : ctx.parsed.y;
              return numberFormat.format(v);
            },
          },
        },
      },
      scales: isRound
        ? {}
        : {
            x: { ticks: { color: textColor }, grid: { color: gridColor } },
            y: {
              beginAtZero: true,
              ticks: {
                color: textColor,
                callback: (v: string | number) => numberFormat.format(Number(v)),
                precision: data?.aggregation && data.aggregation !== 'count' ? undefined : 0,
              },
              grid: { color: gridColor },
            },
          },
    };
    const valueLabels = {
      id: 'dvValueLabels',
      afterDatasetsDraw: (chart: ChartInstance) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        ctx.save();
        ctx.font = '600 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        meta.data.forEach((element, index) => {
          const value = values[index];
          if (value === undefined || (isRound && value === 0)) return;
          const text = numberFormat.format(value);
          const pos = element.tooltipPosition(true);
          if (isRound) {
            ctx.fillStyle = '#fff';
            ctx.fillText(text, pos.x ?? 0, pos.y ?? 0);
          } else {
            ctx.fillStyle = textColor;
            if (type === 'horizontal-bar') {
              const bar = element as unknown as { x: number; y: number };
              ctx.textAlign = 'left';
              ctx.fillText(text, bar.x + 6, bar.y);
              ctx.textAlign = 'center';
            } else {
              ctx.fillText(text, pos.x ?? 0, (pos.y ?? 0) - 12);
            }
          }
        });
        ctx.restore();
      },
    };

    if (this.chart) {
      this.chart.data.labels = labels;
      this.chart.data.datasets = [dataset] as never;
      this.chart.options = options as never;
      this.chart.update();
    } else {
      this.chart = new Chart(canvas, {
        type: kind,
        data: { labels, datasets: [dataset] as never },
        options: options as never,
        plugins: [valueLabels],
      });
    }
  }

  private patch(update: Record<string, unknown>) {
    if (this.view.readonly$.value) return;
    this.view.dataUpdate(() => update as never);
  }

  private selectControl(
    label: string,
    value: string,
    items: [string, string][],
    onChange: (value: string) => void
  ) {
    const disabled = this.view.readonly$.value;
    return html`<label
      >${label}
      <select
        .value=${value}
        ?disabled=${disabled}
        @change=${(e: Event) => onChange((e.target as HTMLSelectElement).value)}
        @pointerdown=${(e: Event) => e.stopPropagation()}
        @keydown=${(e: Event) => e.stopPropagation()}
      >
        ${items.map(
          ([key, text]) =>
            html`<option value=${key} ?selected=${key === value}>
              ${text}
            </option>`
        )}
      </select></label
    >`;
  }

  override render() {
    const view = this.view;
    const data = view.data$.value;
    const properties = view.properties$.value;
    const categoryItems = properties.map(
      p => [p.id, p.name$.value || 'Ohne Namen'] as [string, string]
    );
    const numberProps = properties.filter(p =>
      NUMERIC_TYPES.has(p.type$.value as string)
    );
    const aggregation = data?.aggregation ?? 'count';
    const metricValue =
      aggregation === 'count' || !data?.valuePropertyId
        ? 'count'
        : `${aggregation}:${data.valuePropertyId}`;
    const metricItems: [string, string][] = [['count', 'Anzahl der Zeilen']];
    for (const p of numberProps) {
      const name = p.name$.value || 'Ohne Namen';
      metricItems.push([`sum:${p.id}`, `Summe: ${name}`]);
      metricItems.push([`avg:${p.id}`, `Mittelwert: ${name}`]);
    }
    const points = view.chartData$.value;
    const height = HEIGHTS[data?.height ?? 'M'];

    return html`
      ${this.logic.headerWidget
        ? renderUniLit(this.logic.headerWidget, { dataViewLogic: this.logic })
        : nothing}
      <button
        class="dv-chart-toggle"
        @pointerdown=${(e: Event) => e.stopPropagation()}
        @click=${() => (this.controlsOpen = !this.controlsOpen)}
      >
        ⚙ Diagramm-Einstellungen ${this.controlsOpen ? '▴' : '▾'}
      </button>
      <div class="dv-chart-controls" ?hidden=${!this.controlsOpen}>
        ${this.selectControl(
          'Diagramm',
          data?.chartType ?? 'bar',
          TYPE_LABELS,
          v => this.patch({ chartType: v as ChartType })
        )}
        ${this.selectControl(
          'Kategorie',
          data?.categoryPropertyId ?? '',
          categoryItems,
          v => this.patch({ categoryPropertyId: v })
        )}
        ${this.selectControl('Wert', metricValue, metricItems, v => {
          if (v === 'count') {
            this.patch({ aggregation: 'count', valuePropertyId: undefined });
          } else {
            const [agg, id] = v.split(':');
            this.patch({
              aggregation: agg as ChartAggregation,
              valuePropertyId: id,
            });
          }
        })}
        ${this.selectControl(
          'Sortierung',
          data?.sort ?? 'natural',
          SORT_LABELS,
          v => this.patch({ sort: v as ChartSort })
        )}
        ${this.selectControl(
          'Höhe',
          data?.height ?? 'M',
          HEIGHT_LABELS,
          v => this.patch({ height: v as ChartHeight })
        )}
      </div>
      <div class="dv-chart-canvas" style="height:${height}px">
        ${points.length
          ? html`<canvas></canvas>`
          : html`<div class="dv-chart-empty">
              Keine Daten für dieses Diagramm.
            </div>`}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-data-view-chart': ChartViewUI;
  }
}
